// utils/nonce.ts
import CryptoJS from "crypto-js";

// --- ENV Setup ---
const SECRET = process.env.NEXT_PUBLIC_NONCE_SECRET || "";
const IV_STRING = process.env.NEXT_PUBLIC_NONCE_IV || "";
const EXPIRY_SECONDS = parseInt(
  process.env.NEXT_PUBLIC_NONCE_EXPIRY || "30",
  10
);

// --- Key and IV Validation ---
if (SECRET.length !== 32) {
  throw new Error(
    "NEXT_PUBLIC_NONCE_SECRET must be exactly 32 characters long."
  );
}
if (IV_STRING.length !== 16) {
  throw new Error("NEXT_PUBLIC_NONCE_IV must be exactly 16 characters long.");
}

const ENCRYPTION_KEY = CryptoJS.enc.Utf8.parse(SECRET); // 32 bytes
const IV = CryptoJS.enc.Utf8.parse(IV_STRING); // 16 bytes

// --- Nonce Functions ---
export function generateNonce(): string {
  const payload = {
    uuid: crypto.randomUUID(),
    timestamp: Date.now(),
  };

  return encryptObject(payload);
}

export function validateNonce(nonce: string): boolean {
  const decrypted = decryptObject(nonce);
  if (!decrypted) return false;

  const { timestamp } = decrypted as { timestamp: number };
  return Date.now() - timestamp <= EXPIRY_SECONDS * 1000;
}

export function encryptObject(obj: object): string {
  const jsonString = JSON.stringify(obj);

  const encrypted = CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY, {
    iv: IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  // Return base64-encoded ciphertext
  return CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
}

export function decryptObject(ciphertext: string): object | null {
  try {
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(ciphertext),
    });

    const decrypted = CryptoJS.AES.decrypt(cipherParams, ENCRYPTION_KEY, {
      iv: IV,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
}
